import type { AmCostLine, AmRecommendation } from './types';

/**
 * Recommendation pricing, on the browser side.
 *
 * The API already parses `CostLines` and keeps the scalar Cost/Priority in step
 * with them, so these helpers exist for the two things the browser has to do
 * itself: split a filtered set of recommendations into One-time / Monthly /
 * Annual buckets, and decide how urgent a multi-line recommendation is.
 *
 * The fallback to the scalar columns is kept here as well. A recommendation
 * fetched from a list endpoint that predates cost lines still has to render.
 */
export const REC_PRIORITIES = ['Critical', 'High', 'Medium', 'Low'] as const;
export const REC_COST_TYPES = ['One-time', 'Monthly', 'Annual'] as const;
export const REC_STATUSES = [
  'Proposed',
  'Approved',
  'In Progress',
  'Completed',
  'Declined',
] as const;

/** Still in play. Completed and declined work is settled, not pipeline. */
export const OPEN_REC_STATUSES = ['Proposed', 'Approved', 'In Progress'] as const;

const RANK: Record<string, number> = { Critical: 4, High: 3, Medium: 2, Low: 1 };
export const priorityRank = (p: string | null | undefined): number => RANK[p ?? ''] ?? 0;

export const blankCostLine = (): AmCostLine => ({
  priority: 'Medium',
  cost: 0,
  costType: 'One-time',
});

function normalize(line: Partial<AmCostLine>): AmCostLine {
  return {
    priority: (REC_PRIORITIES as readonly string[]).includes(line.priority ?? '')
      ? (line.priority as AmCostLine['priority'])
      : 'Medium',
    cost: Number(line.cost) || 0,
    costType: (REC_COST_TYPES as readonly string[]).includes(line.costType ?? '')
      ? (line.costType as AmCostLine['costType'])
      : 'One-time',
  };
}

export function costLinesOf(rec: Partial<AmRecommendation> | null | undefined): AmCostLine[] {
  const lines = rec?.CostLines;
  if (Array.isArray(lines) && lines.length > 0) return lines.map(normalize);
  return [
    normalize({
      priority: (rec?.Priority ?? undefined) as AmCostLine['priority'] | undefined,
      cost: rec?.Cost ?? 0,
      costType: (rec?.CostType ?? undefined) as AmCostLine['costType'] | undefined,
    }),
  ];
}

export const totalCost = (rec: Partial<AmRecommendation>): number =>
  costLinesOf(rec).reduce((sum, l) => sum + l.cost, 0);

/** A recommendation is as urgent as its most urgent line. */
export const topPriority = (rec: Partial<AmRecommendation>): string =>
  costLinesOf(rec).reduce<string>(
    (best, l) => (priorityRank(l.priority) > priorityRank(best) ? l.priority : best),
    'Low',
  );

export interface Buckets {
  oneTime: number;
  monthly: number;
  annual: number;
}

/**
 * Split across One-time / Monthly / Annual, counting every line.
 *
 * Summing `Cost` alone would put a recommendation's monthly run cost in the
 * same total as its one-time build cost, which is the mistake migration 015
 * exists to record.
 */
export function bucketTotals(recs: Partial<AmRecommendation>[] | null | undefined): Buckets {
  const totals: Buckets = { oneTime: 0, monthly: 0, annual: 0 };
  for (const rec of recs ?? []) {
    for (const line of costLinesOf(rec)) {
      if (line.costType === 'Monthly') totals.monthly += line.cost;
      else if (line.costType === 'Annual') totals.annual += line.cost;
      else totals.oneTime += line.cost;
    }
  }
  return totals;
}

/** Annualised value of a bucket split, for ranking one book of work against
 *  another. Monthly is worth twelve times its face figure over a year. */
export const annualisedValue = (b: Buckets): number => b.oneTime + b.monthly * 12 + b.annual;

const money = (n: number): string =>
  n.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

/** Flat text for email bodies: "$2,000 One-time + $150 Monthly". */
export const costSummaryText = (rec: Partial<AmRecommendation>): string =>
  costLinesOf(rec)
    .map((l) => `${money(l.cost)} ${l.costType}`)
    .join(' + ');

export const isOpenRec = (status: string | null | undefined): boolean =>
  (OPEN_REC_STATUSES as readonly string[]).includes(status ?? 'Proposed');

/**
 * What a recommendation costs in each year of a budget, from the year it is
 * planned in (BudgetYear, migration 194): its one-time cost in that year, and
 * a monthly or annual cost every year from then on — a service bought in 2028
 * is still being paid for in 2030. Nothing before that year, and nothing at all
 * when it is not in the budget.
 */
export function budgetByYear(rec: Partial<AmRecommendation>, years: number[]): number[] {
  const from = rec.BudgetYear;
  if (!from) return years.map(() => 0);
  const b = bucketTotals([rec]);
  const yearly = b.monthly * 12 + b.annual;
  return years.map((y) => (y < from ? 0 : (y === from ? b.oneTime : 0) + yearly));
}
